import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Target, Heart, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const About = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_em-fitness/artifacts/ltdpykju_IMG_7113.png"
            alt="Coach Massa"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Rencontrez <span className="text-red-600">Coach Massa</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl">
            Plus de 17 ans d'expérience au service de votre transformation
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Coach Massa - <span className="text-red-600">Aujourd'hui</span>
              </h2>
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  Avec <strong className="text-white">plus de 17 ans d'expérience dans le sport</strong> et{' '}
                  <strong className="text-white">6 ans de coaching professionnel</strong>, Coach Massa met son expertise 
                  et sa passion au service de celles et ceux qui souhaitent transformer leur corps et améliorer leur performance.
                </p>
                <p>
                  <strong className="text-white">Ancien footballeur professionnel</strong>, il a développé une discipline, 
                  une rigueur et une mentalité de gagnant qu'il transmet aujourd'hui à ses clients à travers un 
                  accompagnement structuré et motivant.
                </p>
                <p>
                  <strong className="text-white">Certifié EREPS niveau 4</strong>, Coach Massa propose un coaching personnalisé, 
                  adapté à chaque objectif : perte de poids, transformation physique, prise de masse, amélioration des 
                  performances ou remise en forme.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_em-fitness/artifacts/3c6j8e0w_IMG_7111.jpeg"
                alt="Coach Massa"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>

          {/* Mission Statement */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-12 text-center mb-20">
            <Heart className="w-16 h-16 text-white mx-auto mb-6" />
            <h3 className="text-3xl font-bold text-white mb-4">Sa Mission</h3>
            <p className="text-2xl text-white/95 font-semibold mb-4">
              Vous aider à devenir la meilleure version de vous-même.
            </p>
            <p className="text-xl text-white/90">
              Rien ne le motive plus que de voir ses clients évoluer, se dépasser et obtenir des résultats concrets.
            </p>
          </div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {[
              {
                icon: Award,
                title: 'Expertise',
                description: 'Certifié EREPS niveau 4 avec 17 ans d\'expérience'
              },
              {
                icon: Target,
                title: 'Personnalisation',
                description: 'Chaque séance est pensée pour VOS objectifs'
              },
              {
                icon: TrendingUp,
                title: 'Résultats',
                description: 'Des transformations concrètes et mesurables'
              },
              {
                icon: Heart,
                title: 'Passion',
                description: 'Un engagement total pour votre réussite'
              }
            ].map((value, index) => (
              <div
                key={index}
                className="bg-zinc-900 p-8 rounded-lg text-center hover:bg-zinc-800 transition-all hover:scale-105"
              >
                <value.icon className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-3">{value.title}</h4>
                <p className="text-gray-400">{value.description}</p>
              </div>
            ))}
          </div>

          {/* Professional Background */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_em-fitness/artifacts/3c6j8e0w_IMG_7111.jpeg"
                alt="Coach Massa sur le terrain"
                className="rounded-lg shadow-2xl"
              />
            </div>

            <div className="order-1 md:order-2">
              <h3 className="text-3xl font-bold text-white mb-6">
                Du <span className="text-red-600">Terrain</span> à la Salle
              </h3>
              <div className="space-y-4 text-gray-300 text-lg">
                <p>
                  Ancien footballeur professionnel, Coach Massa a connu les exigences du haut niveau. 
                  Cette expérience lui a forgé une discipline de fer et une compréhension profonde de 
                  la performance sportive.
                </p>
                <p>
                  Aujourd'hui, il met cette expertise au service de tous : débutants, personnes souhaitant 
                  perdre du poids, prendre de la masse, ou améliorer leurs performances. Hommes, femmes, 
                  enfants, personnes âgées - chacun bénéficie d'un accompagnement adapté.
                </p>
                <p className="text-xl font-semibold text-white">
                  Avec Coach Massa, chaque séance est pensée pour vous rapprocher de vos objectifs et 
                  révéler votre plein potentiel. 💪
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black border-t border-red-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Prêt à Commencer Votre Parcours ?
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            Rejoignez Coach Massa et transformez votre vie dès aujourd'hui
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
              Réserver une Séance
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
